import { useMemo, useState, type ComponentProps } from "react";
import { useQuery } from "@tanstack/react-query";
import { AppointmentTimeSlotStep } from "@baluu/ui/components/checkout/appointment-time-slot-step";
import { bookingJson } from "../../api";
import { Notice } from "../../parts";
import {
  buildPricingOptions,
  appointmentTimezone,
  monthRange,
  resolveAppointmentStart,
  scheduleSlotsOf,
} from "./appointment";
import type { ApiCourse, ApiTicketType } from "./apiTypes";

// The day-and-time picker for rescheduling an appointment booking: the
// checkout's own appointment step (the same one customers booked with in
// this package), fed a month of the listing's free slots at a time, as v1's
// DateBasePricingOptionStep is. Like v1 it never asks for a pricing option:
// the booking keeps its tickets, only its time moves.

type StepProps = ComponentProps<typeof AppointmentTimeSlotStep>;

type SlotsResponse = {
  timezone?: string;
  availableDates?: NonNullable<StepProps["availableDates"]>;
  locationsById?: Record<string, { name: string }>;
  hasMultipleLocations?: boolean;
};

export type AppointmentChoice = { startDateTime: string; duration?: number } | null;

export function AppointmentPicker({
  api,
  course,
  ticketTypes,
  currency,
  disabled,
  onChange,
}: {
  api: string;
  course: ApiCourse;
  ticketTypes: ApiTicketType[];
  currency?: string;
  disabled?: boolean;
  onChange: (choice: AppointmentChoice) => void;
}) {
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [date, setDate] = useState<Date | null>(null);
  const [time, setTime] = useState<number | null>(null);
  const [option, setOption] = useState<number | null>(null);

  const { startDate, endDate } = monthRange(month);
  const slots = useQuery({
    queryKey: ["booking", "reschedule-slots", api, course._id, startDate],
    queryFn: () =>
      bookingJson<SlotsResponse>(
        `${api}/widget/courses/${encodeURIComponent(course._id)}/available-slots?startDate=${startDate}&endDate=${endDate}`,
      ),
  });

  const pricingOptions = useMemo(() => buildPricingOptions(scheduleSlotsOf(course), ticketTypes), [course, ticketTypes]);
  const availableDates = slots.data?.availableDates ?? [];
  const timezone = appointmentTimezone(slots.data?.timezone, course.timezone);

  function emit(nextDate: Date | null, nextTime: number | null) {
    onChange(resolveAppointmentStart({ availableDates, date: nextDate, time: nextTime, timezone }));
  }

  return (
    <div className={disabled ? "pointer-events-none opacity-60" : undefined} aria-disabled={disabled || undefined}>
      {slots.isError && (
        <div className="mb-4">
          <Notice tone="error">We couldn’t load the available times. Please try again.</Notice>
        </div>
      )}
      <AppointmentTimeSlotStep
        course={course}
        isGroupSchedule={Boolean(course.isGroupSchedule)}
        pricingOptions={pricingOptions}
        availableDates={availableDates}
        isLoading={slots.isLoading}
        currency={currency || course.currency}
        timezone={timezone}
        currentMonth={month}
        onMonthChange={(next) => {
          // A new month clears the choice, as v1 does.
          setMonth(new Date(next.getFullYear(), next.getMonth(), 1));
          setDate(null);
          setTime(null);
          setOption(null);
          onChange(null);
        }}
        selectedDate={date}
        selectedTime={time}
        selectedOption={option}
        hidePricingOptions
        onDateChange={(next) => {
          setDate(next);
          setTime(null);
          emit(next, null);
        }}
        onTimeChange={(next) => {
          setTime(next);
          emit(date, next);
        }}
        onOptionChange={setOption}
        locationsById={slots.data?.locationsById}
        hasMultipleLocations={Boolean(slots.data?.hasMultipleLocations)}
      />
    </div>
  );
}
